import axios, { type AxiosInstance } from 'axios'
import type {
  BillingRunResult,
  EstateSummary,
  InvoiceView,
  OperatorDetail,
  OperatorRole,
  OperatorStatus,
  Page,
  PlanUsage,
  PlanView,
  PlatformAuditEntry,
  PlatformLoginResponse,
  ProvisionRequest,
  ProvisionResult,
  SavePlanRequest,
  TenantDetail,
  TenantSummary,
} from '../types'

const BASE_URL = `${import.meta.env.VITE_API_BASE_URL ?? '/api'}/platform`

/**
 * The operator's token, in memory only.
 *
 * <p>Not sessionStorage and not a cookie, matching what the tenant app does with its own
 * access token — and more so here, because this one can see every customer on the
 * installation. The cost is that a page reload signs the operator out again. That is the
 * right trade for a session the backend already limits to thirty minutes with no refresh
 * token: an operator session is a task, not a working day.
 */
let operatorToken: string | null = null

export function setOperatorToken(token: string | null) {
  operatorToken = token
}

export function getOperatorToken() {
  return operatorToken
}

/**
 * A separate axios instance, deliberately.
 *
 * <p>Sharing the tenant client would mean one interceptor deciding which of two tokens to
 * attach, and the day it decides wrong an operator's token goes to a customer's API or the
 * reverse. Two clients cannot make that mistake. This one also sends no credentials: there
 * is no refresh cookie in this realm.
 */
export const platformClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
})

platformClient.interceptors.request.use((config) => {
  if (operatorToken) {
    config.headers.Authorization = `Bearer ${operatorToken}`
  }
  config.headers['X-Correlation-Id'] = crypto.randomUUID()
  return config
})

export const platformApi = {
  async login(email: string, password: string): Promise<PlatformLoginResponse> {
    const { data } = await platformClient.post<PlatformLoginResponse>('/auth/login', {
      email,
      password,
    })
    return data
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await platformClient.post('/auth/password', { currentPassword, newPassword })
  },

  async tenants(): Promise<TenantSummary[]> {
    const { data } = await platformClient.get<TenantSummary[]>('/tenants')
    return data
  },

  async estate(): Promise<EstateSummary> {
    const { data } = await platformClient.get<EstateSummary>('/tenants/estate')
    return data
  },

  async tenant(id: string): Promise<TenantDetail> {
    const { data } = await platformClient.get<TenantDetail>(`/tenants/${id}`)
    return data
  },

  async plans(): Promise<PlanView[]> {
    const { data } = await platformClient.get<PlanView[]>('/plans')
    return data
  },

  async provision(request: ProvisionRequest): Promise<ProvisionResult> {
    const { data } = await platformClient.post<ProvisionResult>('/tenants', request)
    return data
  },

  async changePlan(id: string, planCode: string, seats: number | null, reason: string): Promise<void> {
    await platformClient.post(`/tenants/${id}/plan`, { planCode, seats, reason })
  },

  async suspend(id: string, reason: string): Promise<void> {
    await platformClient.post(`/tenants/${id}/suspend`, { reason })
  },

  async resume(id: string): Promise<void> {
    await platformClient.post(`/tenants/${id}/resume`)
  },

  async cancel(id: string, reason: string, retentionDays: number): Promise<void> {
    await platformClient.post(`/tenants/${id}/cancel`, { reason, retentionDays })
  },

  async extendTrial(id: string, days: number, reason: string): Promise<void> {
    await platformClient.post(`/tenants/${id}/extend-trial`, { days, reason })
  },

  // --------------------------------------------------------------- operators

  async operators(): Promise<OperatorDetail[]> {
    const { data } = await platformClient.get<OperatorDetail[]>('/auth/operators')
    return data
  },

  async inviteOperator(email: string, fullName: string, role: OperatorRole): Promise<void> {
    await platformClient.post('/auth/operators', { email, fullName, role })
  },

  async setOperatorRole(id: string, role: OperatorRole): Promise<OperatorDetail> {
    const { data } = await platformClient.put<OperatorDetail>(`/auth/operators/${id}/role`, { role })
    return data
  },

  async setOperatorStatus(
    id: string,
    status: OperatorStatus,
    reason?: string,
  ): Promise<OperatorDetail> {
    const { data } = await platformClient.put<OperatorDetail>(`/auth/operators/${id}/status`, {
      status,
      reason: reason ?? null,
    })
    return data
  },

  async setOperatorPassword(id: string, newPassword: string): Promise<void> {
    await platformClient.post(`/auth/operators/${id}/password`, { newPassword })
  },

  // ------------------------------------------------------------------- audit

  async audit(tenantId?: string, page = 0, size = 50): Promise<Page<PlatformAuditEntry>> {
    const { data } = await platformClient.get<Page<PlatformAuditEntry>>('/audit', {
      params: { tenantId, page, size },
    })
    return data
  },

  // ------------------------------------------------------------------- plans

  async createPlan(code: string, plan: SavePlanRequest): Promise<PlanView> {
    const { data } = await platformClient.post<PlanView>('/plans', { code, plan })
    return data
  },

  async updatePlan(code: string, plan: SavePlanRequest): Promise<PlanView> {
    const { data } = await platformClient.put<PlanView>(`/plans/${code}`, plan)
    return data
  },

  async setPlanOffered(code: string, offered: boolean): Promise<PlanView> {
    const { data } = await platformClient.post<PlanView>(`/plans/${code}/offered`, null, {
      params: { offered },
    })
    return data
  },

  async deletePlan(code: string): Promise<void> {
    await platformClient.delete(`/plans/${code}`)
  },

  async planUsage(code: string): Promise<PlanUsage> {
    const { data } = await platformClient.get<PlanUsage>(`/plans/${code}/usage`)
    return data
  },

  // ----------------------------------------------------------------- billing

  async invoices(tenantId?: string): Promise<InvoiceView[]> {
    const { data } = await platformClient.get<InvoiceView[]>('/billing/invoices', {
      params: { tenantId },
    })
    return data
  },

  async runBilling(
    period: string,
    vatPercent: number,
    dueInDays: number,
    commit: boolean,
  ): Promise<BillingRunResult> {
    const { data } = await platformClient.post<BillingRunResult>('/billing/runs', {
      period,
      vatPercent,
      dueInDays,
      commit,
    })
    return data
  },

  async issueInvoice(id: string): Promise<InvoiceView> {
    const { data } = await platformClient.post<InvoiceView>(`/billing/invoices/${id}/issue`)
    return data
  },

  async markInvoicePaid(
    id: string,
    paidOn: string,
    paymentReference: string,
  ): Promise<InvoiceView> {
    const { data } = await platformClient.post<InvoiceView>(`/billing/invoices/${id}/paid`, {
      paidOn,
      paymentReference,
    })
    return data
  },

  async voidInvoice(id: string, reason: string): Promise<InvoiceView> {
    const { data } = await platformClient.post<InvoiceView>(`/billing/invoices/${id}/void`, {
      reason,
    })
    return data
  },
}
